import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Phone, Mail, MapPin, ArrowUpRight, ArrowLeft, Clock, CheckCircle2, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SERVICES, PHONE_DISPLAY, PHONE_TEL, EMAIL } from "@/lib/data";
import useSeo from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MESSAGE = 1000;

export default function Contact() {
  useSeo({
    title: "Contact & Free Quote — RSR Australia",
    description: "Tell us the job. Get a free quote within one business day. Or call 0410 850 550 — someone always picks up.",
  });
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get("service") || "";

  const [step, setStep] = useState(initialService ? 2 : 1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: initialService, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (initialService) {
      setForm(f => ({ ...f, service: initialService }));
      setStep(2);
    }
  }, [initialService]);

  const pct = useMemo(() => {
    if (done) return 100;
    let filled = 0;
    if (form.service) filled += 25;
    if (form.name.trim()) filled += 25;
    if (form.email.trim()) filled += 25;
    if (form.message.trim().length > 15) filled += 25;
    return filled;
  }, [form, done]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const pickService = (name) => {
    setForm(f => ({ ...f, service: name }));
    setStep(2);
  };

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please call us or try again.";
      toast.error(typeof detail === "string" ? detail : "Could not send your enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", service: "", message: "" });
    setStep(1);
    setDone(false);
  };

  return (
    <>
      <section data-testid="contact-hero" className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-20 pb-12 lg:pt-28 lg:pb-16 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="overline">Contact</p>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] font-extrabold">
              Tell us the job. <br /> <span className="text-accent">We'll handle the rest.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="text-base text-muted-foreground">
              Most quotes back within one business day. For urgent jobs, give us a call — someone always picks up.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          {/* Form */}
          <div className="col-span-12 lg:col-span-7">
            <div className="border border-border bg-card p-7 lg:p-10">
              {/* Progress */}
              {!done && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 text-xs">
                      <StepBadge n={1} label="Pick a service" active={step >= 1} done={step > 1} onClick={() => setStep(1)} />
                      <ChevronRight className="w-3 h-3 text-foreground/30" />
                      <StepBadge n={2} label="Your job details" active={step === 2} done={false} onClick={() => form.service && setStep(2)} />
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums">{pct}%</div>
                  </div>
                  <div className="h-1 bg-secondary overflow-hidden" data-testid="contact-progress">
                    <div className="h-full bg-accent transition-all duration-500 ease-out" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )}

              {done ? (
                <div data-testid="contact-success" className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-accent" strokeWidth={1.5} />
                  <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tighter">Thanks — we've got it.</h2>
                  <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                    A coordinator will be in touch within one business day. For urgent jobs, give us a call.
                  </p>
                  <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <a href={`tel:${PHONE_TEL}`} data-testid="contact-success-call" className="inline-flex items-center gap-2 bg-accent text-white px-5 py-3 text-sm font-semibold hover:bg-accent/90 transition-colors">
                      <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
                    </a>
                    <button data-testid="contact-send-another" onClick={resetForm} className="inline-flex items-center gap-2 border border-border hover:border-foreground/40 px-5 py-3 text-sm font-semibold transition-colors">
                      Send another enquiry
                    </button>
                  </div>
                </div>
              ) : step === 1 ? (
                <div data-testid="contact-step-1" className="animate-fade-up">
                  <p className="overline">Step 1 · Pick a service</p>
                  <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">What's the job about?</h2>
                  <p className="mt-3 text-sm text-muted-foreground">Tap a tile to pre-fill your enquiry. Not sure? Pick "Multiple / not sure" and we'll work it out on the call.</p>

                  <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {SERVICES.map((s) => {
                      const active = form.service === s.name;
                      return (
                        <button
                          key={s.slug}
                          type="button"
                          data-testid={`contact-chip-${s.slug}`}
                          onClick={() => pickService(s.name)}
                          className={`text-left p-3.5 border transition-all duration-200 ${
                            active
                              ? "border-accent bg-accent text-white"
                              : "border-border bg-background hover:border-accent/50 hover:-translate-y-0.5"
                          }`}
                        >
                          <div className={`text-[10px] font-bold tracking-[0.22em] uppercase ${active ? "text-white/80" : "text-accent"}`}>{s.accent}</div>
                          <div className={`mt-1 font-display text-sm font-bold leading-tight ${active ? "text-white" : "text-foreground"}`}>{s.name}</div>
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      data-testid="contact-chip-not-sure"
                      onClick={() => pickService("Multiple / not sure")}
                      className={`text-left p-3.5 border transition-all duration-200 ${
                        form.service === "Multiple / not sure"
                          ? "border-accent bg-accent text-white"
                          : "border-dashed border-accent/40 bg-background hover:border-accent hover:-translate-y-0.5"
                      }`}
                    >
                      <div className={`text-[10px] font-bold tracking-[0.22em] uppercase ${form.service === "Multiple / not sure" ? "text-white/80" : "text-accent"}`}>+</div>
                      <div className={`mt-1 font-display text-sm font-bold leading-tight ${form.service === "Multiple / not sure" ? "text-white" : "text-foreground"}`}>Multiple / not sure</div>
                    </button>
                  </div>

                  <div className="mt-8 flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">{form.service ? <span><span className="text-accent font-semibold">{form.service}</span> selected</span> : "Pick a service to continue"}</p>
                    <button
                      type="button"
                      data-testid="contact-step-1-next"
                      disabled={!form.service}
                      onClick={() => setStep(2)}
                      className="inline-flex items-center gap-2 bg-accent text-white px-5 py-3 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-accent/90 transition-colors"
                    >
                      Continue <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" noValidate className="space-y-6 animate-fade-up">
                  <button
                    type="button"
                    data-testid="contact-step-back"
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground uppercase tracking-[0.22em] mb-2"
                  >
                    <ArrowLeft className="w-3 h-3" /> Change service
                  </button>

                  <div>
                    <p className="overline">Step 2 · Your job details</p>
                    <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">A bit about you and the job.</h2>
                    {form.service && (
                      <div className="mt-4 inline-flex items-center gap-2 bg-accent/10 text-accent border border-accent/30 px-3 py-1.5 text-xs font-semibold">
                        <CheckCircle2 className="w-3.5 h-3.5" /> {form.service}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field id="name" label="Your name" required value={form.name} onChange={onChange("name")} placeholder="Full name" testid="contact-name" />
                    <Field id="email" label="Email" type="email" required value={form.email} onChange={onChange("email")} placeholder="you@example.com" testid="contact-email" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field id="phone" label="Phone (optional)" value={form.phone} onChange={onChange("phone")} placeholder="04xx xxx xxx" testid="contact-phone" />
                    <Field id="suburb" label="Suburb (optional)" placeholder="e.g. Newtown 2042" />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-foreground/70">What's the job?</Label>
                      <span className={`text-xs tabular-nums ${form.message.length > MAX_MESSAGE * 0.9 ? "text-accent" : "text-muted-foreground"}`}>
                        {form.message.length}/{MAX_MESSAGE}
                      </span>
                    </div>
                    <Textarea
                      id="message"
                      data-testid="contact-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, MAX_MESSAGE) })}
                      placeholder="A short description of the work, your address suburb, and any preferred dates."
                      required
                      rows={6}
                      className="bg-background border-border focus:border-accent focus-visible:ring-1 focus-visible:ring-accent/30"
                    />
                  </div>

                  <div className="flex items-center gap-3 text-xs text-muted-foreground border-t border-border pt-5">
                    <Clock className="w-3.5 h-3.5 text-accent" />
                    <span>Typical reply: <span className="text-foreground font-semibold">within one business day</span></span>
                  </div>

                  <div className="pt-2 flex flex-wrap items-center gap-3 justify-between">
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="group inline-flex items-center gap-3 bg-accent text-white px-7 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? "Sending..." : "Send enquiry"}
                      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                    <p className="text-xs text-muted-foreground">No spam. We only use your details to reply about this job.</p>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Side info */}
          <aside className="col-span-12 lg:col-span-5 space-y-5">
            <div className="border border-border bg-background p-7">
              <p className="overline">Direct lines</p>
              <div className="mt-5 space-y-4">
                <a href={`tel:${PHONE_TEL}`} data-testid="contact-side-call" className="flex items-start gap-4 group">
                  <Phone className="w-5 h-5 text-accent flex-shrink-0 mt-1" strokeWidth={1.7} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Call</div>
                    <div className="mt-1 font-display text-2xl font-extrabold tracking-tight group-hover:text-accent transition-colors">{PHONE_DISPLAY}</div>
                  </div>
                </a>
                <a href={`mailto:${EMAIL}`} data-testid="contact-side-email" className="flex items-start gap-4 group">
                  <Mail className="w-5 h-5 text-accent flex-shrink-0 mt-1" strokeWidth={1.7} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</div>
                    <div className="mt-1 text-lg font-semibold group-hover:text-accent transition-colors break-all">{EMAIL}</div>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <Clock className="w-5 h-5 text-accent flex-shrink-0 mt-1" strokeWidth={1.7} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Hours</div>
                    <div className="mt-1 text-sm text-foreground/90">Mon–Sat 7am–7pm · Sunday for urgent jobs</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-1" strokeWidth={1.7} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Service area</div>
                    <div className="mt-1 text-sm text-foreground/90">Major metro and regional centres across Australia. Tell us your suburb in the enquiry.</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-border p-7 bg-card">
              <p className="overline">Quick links</p>
              <ul className="mt-4 space-y-2 text-sm">
                <li><Link to="/services" className="text-foreground/85 hover:text-accent">Browse all services</Link></li>
                <li><Link to="/gallery" className="text-foreground/85 hover:text-accent">See our recent work</Link></li>
                <li><Link to="/about" className="text-foreground/85 hover:text-accent">About RSR Australia</Link></li>
              </ul>
            </div>

            <div className="border-2 border-accent bg-accent/5 p-7">
              <p className="overline">Urgent?</p>
              <div className="mt-3 font-display text-2xl font-extrabold tracking-tight">Skip the form.</div>
              <p className="mt-2 text-sm text-foreground/80">For urgent property repairs, the fastest path is a phone call.</p>
              <a href={`tel:${PHONE_TEL}`} data-testid="contact-urgent-call" className="mt-5 inline-flex items-center gap-2 bg-accent text-white px-5 py-3 text-sm font-semibold hover:bg-accent/90 transition-colors">
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function Field({ id, label, type = "text", required = false, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.18em] text-foreground/70">{label}{required ? " *" : ""}</Label>
      <Input
        id={id}
        type={type}
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="mt-2 bg-background border-border focus:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 h-12"
      />
    </div>
  );
}

function StepBadge({ n, label, active, done, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 transition-colors ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
    >
      <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
        active ? "bg-accent text-white" : done ? "bg-foreground text-background" : "bg-secondary text-foreground"
      }`}>
        {done ? <CheckCircle2 className="w-3 h-3" /> : n}
      </span>
      <span className="text-xs font-semibold uppercase tracking-[0.18em]">{label}</span>
    </button>
  );
}
